'use client';
import useModal from '@/lib/hooks/useModal';
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import Image from 'next/image';
import LocationIcon from '../Icons/LocationIcon';
import HamburgerIcon from '../Icons/HamburgerIcon';
import Dialog from '../Dialog/Dialog';
import ArrowRight from '../Icons/ArrowRight';
import Link from 'next/link';
import { NavigationItem, NavigationLink } from '@/types/contentful';
import ContentfulLink from '../ContentfulLink/ContentfulLink';
import { NavigationData } from './types';
import { HeaderEyebrow } from '../HeaderEyebrow/HeaderEyebrow';
import HeaderLocationDrawer from '../HeaderEyebrow/HeaderLocationDrawer';
import {
  getHeaderLocationServerSnapshot,
  getHeaderLocationSnapshot,
  subscribeHeaderLocation,
  writeHeaderLocation,
  getHeaderLocationLoading,
} from '@/lib/utils/headerLocationStorage';
import { usePathname } from 'next/navigation';
import { addTrailingSlashToInternalUrl } from '@/lib/utils/trailingSlash';

export default function NavigationMobile({
  data,
  primaryCta,
  callRailCta,
}: {
  data: NavigationData | null;
  primaryCta: NavigationLink | null;
  callRailCta: NavigationLink | null;
}) {
  const SCROLL_HIDE_THRESHOLD = 60;
  const SCROLL_SHOW_THRESHOLD = 10;
  const EYEBROW_TRANSITION_MS = 400;
  const SCROLL_DELTA_THRESHOLD = 4;

  const { isModalOpen, openModal, closeModal } = useModal();
  const [subMenu, setSubMenu] = useState<NavigationItem[]>([]);
  const [isEyebrowVisible, setIsEyebrowVisible] = useState(true);
  const [isDialogOpened] = useState(false);
  const [selectedItem, setSelectedItem] = useState('');
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const pathname = usePathname();
  const hideHeaderEyebrow = pathname?.startsWith('/ready') ?? false;

  const brandCode = data?.brandCode ?? '';

  const selectedLocation = useSyncExternalStore(
    (callback) => subscribeHeaderLocation(brandCode, callback),
    () => getHeaderLocationSnapshot(brandCode),
    getHeaderLocationServerSnapshot,
  );

  const isLocationLoading = useSyncExternalStore(
    (callback) => subscribeHeaderLocation(brandCode, callback),
    getHeaderLocationLoading,
    () => true,
  );

  const locationLabel = useMemo(() => {
    if (!selectedLocation) return 'Enter Zip Code';
    return `${selectedLocation.locality}, ${selectedLocation.stateShort}`;
  }, [selectedLocation]);

  const lastScrollYRef = useRef(0);
  const isTransitioningRef = useRef(false);
  const isDialogOpenedRef = useRef(isDialogOpened);

  const navItems = data?.items || [];

  const [prevIsModalOpen, setPrevIsModalOpen] = useState(isModalOpen);

  const scheduleClickRef = useRef(false);

  if (isModalOpen !== prevIsModalOpen) {
    setPrevIsModalOpen(isModalOpen);
    if (!isModalOpen) {
      setSubMenu([]);
    }
  }

  // Sync the isDialogOpened state to the ref so it can be accessed in the scroll handler.
  useEffect(() => {
    isDialogOpenedRef.current = isDialogOpened;
  }, [isDialogOpened]);

  // Scroll handler to show/hide the eyebrow based on scroll direction and position.
  useEffect(() => {
    lastScrollYRef.current = window.scrollY;

    const handleScroll = (e: Event) => {
      if (isTransitioningRef.current || isModalOpen) return;

      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollYRef.current;

      if (Math.abs(delta) < SCROLL_DELTA_THRESHOLD) return;

      if (currentScrollY <= SCROLL_SHOW_THRESHOLD) {
        if (!isEyebrowVisible) {
          setIsEyebrowVisible(true);
        }
        lastScrollYRef.current = currentScrollY;
        return;
      }

      if (currentScrollY > SCROLL_HIDE_THRESHOLD) {
        if (delta > 0) {
          if (!isDialogOpenedRef.current && isEyebrowVisible) {
            setIsEyebrowVisible(false);
          }
        } else if (delta < 0 && !isEyebrowVisible) {
          setIsEyebrowVisible(true);
        }
      }

      lastScrollYRef.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isEyebrowVisible, isModalOpen]);

  // When the eyebrow visibility changes, set a flag to prevent scroll handling during the transition,
  // and reset it after the transition duration.
  useEffect(() => {
    isTransitioningRef.current = true;

    const timer = window.setTimeout(() => {
      isTransitioningRef.current = false;
      lastScrollYRef.current = window.scrollY;
    }, EYEBROW_TRANSITION_MS);

    return () => window.clearTimeout(timer);
  }, [isEyebrowVisible]);

  // The logo bar is rendered twice: once inside the fixed overlay (interactive) and once inside the
  // in-flow placeholder below (inert). `isPlaceholder` strips the interactivity from the copy.
  const renderLogoBar = (isPlaceholder: boolean) => (
    <div className='flex items-center justify-between border-b border-border-global bg-white p-6 transition-transform duration-300'>
      {data?.logo?.src && (
        <Link
          href='/'
          tabIndex={isPlaceholder ? -1 : undefined}
          className='block'
          aria-label='Go to homepage'
        >
          <Image
            src={data.logo.src}
            alt={data.logo.alt || data.logo.title || 'Logo'}
            width={data.logo.width || 111}
            height={data.logo.height || 38}
            className='brand-logo-mobile'
          />
        </Link>
      )}
      <div className='flex items-center'>
        <button
          type='button'
          tabIndex={isPlaceholder ? -1 : undefined}
          onClick={isPlaceholder ? undefined : () => setIsLocationOpen(true)}
          className='flex items-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500/40 rounded-sm max-w-[130px] sm:max-w-[200px]'
        >
          <LocationIcon className='text-brand-icon w-8 h-8 shrink-0' />
          <span
            className={`text-primary-500 copy-small font-semibold truncate transition-opacity duration-50 ${
              isLocationLoading ? 'opacity-0' : 'opacity-100'
            }`}
          >
            {locationLabel}
          </span>
        </button>
        <button
          aria-expanded={isModalOpen}
          aria-controls='mobile-menu'
          aria-label='Open navigation menu'
          tabIndex={isPlaceholder ? -1 : undefined}
          onClick={isPlaceholder ? undefined : openModal}
          className='flex cursor-pointer items-center justify-center px-2 border-2 border-mobile-menu-toggle-icon-border rounded-lg py-4 ml-4'
        >
          <HamburgerIcon className='text-primary-500 h-2 w-6' />
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className='fixed top-0 z-100 block w-full xl:hidden'>
        {!hideHeaderEyebrow && (
          <div
            className={`transition-all duration-400 ease-[cubic-bezier(0.23,1,0.32,1)] overflow-hidden  ${
              isEyebrowVisible ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <HeaderEyebrow
              couponOffer={data?.couponOffer ?? null}
              brandCode={data?.brandCode ?? ''}
            />
          </div>
        )}
        {renderLogoBar(false)}
      </div>

      {/* In-flow placeholder that mirrors the header so it reserves the exact height on the first
          (server-rendered) paint. Being in normal flow prevents content overlap; rendering the real
          logo markup — rather than a JS-measured value — keeps the reserved height tenant-correct
          and avoids the layout shift (CLS) of a 0 -> measured update. The eyebrow is a constant
          h-10 bar, so a plain spacer stands in for it (no need to re-mount HeaderEyebrow's modals). */}
      <div
        aria-hidden
        inert
        className='invisible block w-full xl:hidden'
      >
        {!hideHeaderEyebrow && (
          <div
            className={`overflow-hidden transition-all duration-400 ease-[cubic-bezier(0.23,1,0.32,1)] ${
              isEyebrowVisible ? 'max-h-10' : 'max-h-0'
            }`}
          >
            <div className='h-10' />
          </div>
        )}

        {renderLogoBar(true)}
      </div>
      <Dialog
        open={isModalOpen}
        onClose={closeModal}
        maxWidth='max-w-sm'
        backButton={
          subMenu.length > 0 ? (
            <button
              onClick={() => {
                setSubMenu([]);
                setSelectedItem('');
              }}
              aria-label='Go back to main menu'
            >
              <div className='flex items-center justify-center gap-3'>
                <ArrowRight className='text-brand-icon rotate-180' />
                <span className='heading-6 font-semibold'>Back</span>
              </div>
            </button>
          ) : undefined
        }
        stickyFooter={
          <div
            onPointerDownCapture={(e) => {
              const target = e.target as HTMLElement;
              const link = target.closest('a');

              if (link?.getAttribute('href') === '#schedule') {
                // Make sure the booking widget sees an unlocked page
                // before its click handler runs.
                document.body.style.removeProperty('overflow');
                document.documentElement.style.removeProperty('overflow');
              }
            }}
            onClickCapture={(e) => {
              const target = e.target as HTMLElement;
              const link = target.closest('a');

              if (link?.getAttribute('href') === '#schedule') {
                closeModal();
              }
            }}
            className='bg-white border-t border-border-global p-4 mt-auto grid grid-cols-2 gap-4 w-full'
          >
            {callRailCta ? (
              <ContentfulLink
                data={{
                  ...callRailCta,
                  priority: 'CallRail',
                  context: 'mobile-row',
                  className: 'grow w-full justify-center',
                }}
              />
            ) : null}

            {primaryCta ? (
              <ContentfulLink
                data={{
                  ...primaryCta,
                  priority: 'Primary',
                  context: 'mobile-row',
                  className: 'grow w-full justify-center',
                }}
              />
            ) : null}
          </div>
        }
      >
        <ul className='flex flex-col'>
          {subMenu.length === 0 &&
            navItems.map((item: NavigationItem) => {
              if (item.__typename === 'NavigationMenu') {
                return (
                  <li key={item.id}>
                    <button
                      onClick={() => {
                        setSubMenu(item.items);
                        setSelectedItem(item.menuName || item.title);
                      }}
                      className='flex w-full items-center justify-between py-4 border-b border-border-global cursor-pointer'
                    >
                      <span className='heading-6 text-primary-500'>
                        {item.menuName || item.title}
                      </span>
                      <ArrowRight className='text-brand-icon size-4' />
                    </button>
                  </li>
                );
              }
              if (item.__typename === 'Link' && item.url) {
                return (
                  <li key={item.id}>
                    <Link
                      href={addTrailingSlashToInternalUrl(item.url)}
                      onClick={closeModal}
                      className='flex w-full items-center justify-between py-4 border-b border-border-global cursor-pointer'
                    >
                      <span className='heading-6 text-primary-500'>{item.title}</span>
                      <ArrowRight className='text-brand-icon size-4' />
                    </Link>
                  </li>
                );
              }
              return null;
            })}
          {subMenu.length > 0 && (
            <>
              <li className='py-3 mb-2'>
                <span className='heading-5 text-primary-500'>{selectedItem}</span>
              </li>
              {subMenu.map((item: NavigationItem, index) => {
                if (item.__typename === 'NavigationMenu') {
                  return (
                    <li key={(item.id || 'menu') + '-' + index}>
                      <button
                        onClick={() => {
                          setSubMenu(item.items);
                          setSelectedItem(item.menuName || item.title);
                        }}
                        className='flex w-full items-center justify-between py-4 border-b border-border-global cursor-pointer'
                      >
                        <span className='heading-6 text-primary-500'>
                          {item.menuName || item.title}
                        </span>
                        <ArrowRight className='text-brand-icon size-4' />
                      </button>
                    </li>
                  );
                }
                if (item.__typename === 'Link') {
                  return (
                    <li key={(item.id || 'link') + '-' + index}>
                      <Link
                        href={addTrailingSlashToInternalUrl(item.url) || '#'}
                        onClick={closeModal}
                        className='flex w-full items-center justify-between py-3 border-b border-border-global'
                      >
                        <span className='copy text-font-color'>{item.label}</span>
                        <ArrowRight className='text-brand-icon size-3' />
                      </Link>
                    </li>
                  );
                }
                return null;
              })}
            </>
          )}
        </ul>
      </Dialog>
      <HeaderLocationDrawer
        isOpen={isLocationOpen}
        onClose={() => setIsLocationOpen(false)}
        selectedLocation={selectedLocation}
        onSave={(location) => {
          writeHeaderLocation(brandCode, location);
          setIsLocationOpen(false);
        }}
      />
    </>
  );
}
